<!--
  User preferences — currently the home of the timezone picker. Other
  per-user prefs (date format, week-start day, default workflow view)
  can land in this same page as additional sections.

  Why a separate page from /settings/appearance: appearance is theme
  / visual stuff that doesn't change application behavior. Preferences
  affect how DATA is rendered (timezone, formats) and could grow into
  features like default landing page or session timeout — distinct
  enough to be its own destination.
-->
<template>
  <div class="p-6 max-w-5xl mx-auto space-y-10">
    <header>
      <h1 class="text-3xl font-bold text-app-primary tracking-tight">Preferences</h1>
      <p class="text-app-secondary mt-1.5 text-sm">
        Settings that affect how data is shown to you across PulsarAI.
        Underlying data is always stored in UTC; these settings only
        control how it's rendered on your screen.
      </p>
    </header>

    <!-- ============================================================
         Timezone
         ============================================================ -->
    <section class="space-y-4">
      <div class="flex items-baseline justify-between">
        <h2 class="text-sm font-semibold uppercase tracking-wider text-app-tertiary">
          Timezone
        </h2>
        <span class="text-xs text-app-tertiary">
          {{ isExplicitlySet ? 'Set by you' : 'Auto-detected from your browser' }}
        </span>
      </div>

      <p class="text-sm text-app-secondary">
        Dates and times across reports, dashboards, executions, and
        workflows are rendered in this timezone. The data itself is
        stored in UTC — only the display changes.
      </p>

      <!-- Live preview chip — shows the current time in the
           selected timezone with abbreviation + offset, so the user
           can verify their pick before saving. -->
      <div class="rounded-lg border border-app-divider bg-app-surface px-4 py-3 flex items-center justify-between gap-4">
        <div class="min-w-0">
          <div class="text-[10px] font-semibold uppercase tracking-wider text-app-tertiary">
            Now in {{ pendingTimezone || 'UTC' }}
          </div>
          <div class="mt-1 text-sm font-mono text-app-primary truncate">
            {{ previewNow }}
          </div>
        </div>
        <div class="shrink-0 text-right">
          <div class="text-[10px] font-semibold uppercase tracking-wider text-app-tertiary">
            Abbreviation
          </div>
          <div class="mt-1 text-sm font-mono text-app-primary">
            {{ previewAbbr.abbr }} ({{ previewAbbr.offset }})
          </div>
        </div>
      </div>

      <!-- Picker. Combines a search input with a scrollable list so
           the user can pick from any IANA zone without scrolling
           through 400+ entries when they know what they want. -->
      <div class="rounded-lg border border-app-divider bg-app-surface">
        <div class="flex items-center gap-2 border-b border-app-divider px-3 py-2">
          <Icon name="heroicons:magnifying-glass" class="w-4 h-4 text-app-tertiary" />
          <input
            v-model="search"
            type="text"
            placeholder="Search timezones (e.g. New York, London, Tokyo)…"
            class="flex-1 bg-transparent text-sm text-app-primary placeholder-app-tertiary focus:outline-none"
          />
          <button
            type="button"
            class="text-[11px] font-medium text-indigo-600 dark:text-indigo-400 hover:underline"
            :title="`Detected: ${detectedTimezone}`"
            @click="useBrowserDetected"
          >
            Use my browser's timezone
          </button>
        </div>
        <div class="max-h-72 overflow-y-auto py-1">
          <button
            v-for="tz in filteredTimezones"
            :key="tz"
            type="button"
            class="block w-full px-3 py-1.5 text-left text-sm font-mono transition-colors"
            :class="tz === pendingTimezone
              ? 'bg-indigo-50 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-300'
              : 'text-app-primary hover:bg-app-hover'"
            @click="pendingTimezone = tz"
          >
            <span class="inline-block w-6 mr-1">
              <Icon
                v-if="tz === pendingTimezone"
                name="heroicons:check"
                class="w-3.5 h-3.5"
              />
            </span>
            {{ tz }}
          </button>
          <div
            v-if="filteredTimezones.length === 0"
            class="px-3 py-3 text-xs text-app-tertiary"
          >
            No timezones match "{{ search }}".
          </div>
        </div>
      </div>

      <!-- Save / reset bar -->
      <div class="flex items-center justify-end gap-2">
        <button
          v-if="isExplicitlySet"
          type="button"
          class="text-xs text-app-secondary hover:text-app-primary px-3 py-1.5"
          :disabled="isSaving"
          @click="onClear"
        >
          Reset to browser auto-detect
        </button>
        <button
          type="button"
          class="rounded-md bg-indigo-600 hover:bg-indigo-700 px-3.5 py-1.5 text-sm font-medium text-white shadow-sm disabled:opacity-50 disabled:cursor-not-allowed"
          :disabled="isSaving || pendingTimezone === effectiveTimezone"
          @click="onSave"
        >
          {{ isSaving ? 'Saving…' : 'Save timezone' }}
        </button>
      </div>

      <p
        v-if="saveError"
        class="text-xs text-red-600 dark:text-red-400"
      >
        {{ saveError }}
      </p>
    </section>
  </div>
</template>

<script setup lang="ts">
const {
  effectiveTimezone,
  detectBrowserTimezone,
  timezoneAbbr,
  formatInUserTz: _formatInUserTz,
  setUserTimezone,
  isExplicitlySet,
} = useUserTimezone()

const detectedTimezone = detectBrowserTimezone()

/// In-progress selection. The user picks from the list; we don't
/// commit to Firestore until they hit Save. Lets them try options
/// in the live preview without churning the doc.
const pendingTimezone = ref(effectiveTimezone.value)

/// Re-sync `pendingTimezone` when the saved value changes externally
/// (another tab saving, or the user clicking Reset). Without this,
/// the picker would drift from the actual saved state after a save.
watch(effectiveTimezone, (next) => {
  if (next && pendingTimezone.value !== next && !isSaving.value) {
    pendingTimezone.value = next
  }
})

const search = ref('')

/// All IANA timezones the runtime supports. Most modern browsers ship
/// `Intl.supportedValuesOf('timeZone')` (Chrome 99+, Safari 15.4+,
/// Firefox 93+); older browsers fall back to a hand-curated list of
/// the most common zones so the picker doesn't render empty.
const ALL_TIMEZONES = computed<string[]>(() => {
  try {
    const intlSupported = (Intl as unknown as { supportedValuesOf?: (k: string) => string[] }).supportedValuesOf
    if (typeof intlSupported === 'function') {
      const tzs = intlSupported('timeZone')
      if (Array.isArray(tzs) && tzs.length > 0) return tzs
    }
  }
  catch {
    // Fall through to the fallback list.
  }
  return _FALLBACK_TIMEZONES
})

const filteredTimezones = computed(() => {
  const q = search.value.trim().toLowerCase()
  if (!q) return ALL_TIMEZONES.value
  // Match on the full IANA string AND on a normalized form where
  // underscores become spaces, so "new york" finds America/New_York.
  return ALL_TIMEZONES.value.filter((tz) => {
    const lower = tz.toLowerCase()
    return lower.includes(q) || lower.replace(/_/g, ' ').includes(q)
  })
})

// ----- Live preview ------------------------------------------------

/// Tick the preview every second so the seconds in "Now in …" stay
/// fresh. Stop on unmount to avoid leaking timers.
const previewTick = ref(0)
let timer: ReturnType<typeof setInterval> | null = null
onMounted(() => {
  timer = setInterval(() => { previewTick.value++ }, 1000)
})
onBeforeUnmount(() => {
  if (timer) clearInterval(timer)
})

const previewNow = computed<string>(() => {
  // touch the tick ref so this re-evaluates each second
  void previewTick.value
  if (!pendingTimezone.value) return ''
  try {
    return new Intl.DateTimeFormat(undefined, {
      timeZone: pendingTimezone.value,
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      second: '2-digit',
      hour12: true,
      timeZoneName: 'short',
    }).format(new Date())
  }
  catch {
    return 'Invalid timezone'
  }
})

const previewAbbr = computed(() => {
  // Use the existing helper but against the PENDING tz (not the
  // saved one) so the chip updates as the user picks. Cheap to
  // recompute on every tick.
  void previewTick.value
  try {
    const formatter = new Intl.DateTimeFormat('en-US', {
      timeZone: pendingTimezone.value,
      timeZoneName: 'short',
    })
    const at = new Date()
    const parts = formatter.formatToParts(at)
    const abbr = parts.find((p) => p.type === 'timeZoneName')?.value || pendingTimezone.value
    const utcMs = new Date(at.toLocaleString('en-US', { timeZone: 'UTC' })).getTime()
    const tgtMs = new Date(at.toLocaleString('en-US', { timeZone: pendingTimezone.value })).getTime()
    const offsetMin = (tgtMs - utcMs) / (1000 * 60)
    const sign = offsetMin >= 0 ? '+' : '-'
    const h = Math.floor(Math.abs(offsetMin) / 60).toString().padStart(2, '0')
    const m = (Math.abs(offsetMin) % 60).toString().padStart(2, '0')
    return { abbr, offset: `${sign}${h}:${m}` }
  }
  catch {
    return { abbr: 'UTC', offset: '+00:00' }
  }
})

// ----- Save / reset ----------------------------------------------

const isSaving = ref(false)
const saveError = ref<string | null>(null)

function useBrowserDetected() {
  pendingTimezone.value = detectedTimezone
}

async function onSave() {
  saveError.value = null
  isSaving.value = true
  try {
    await setUserTimezone(pendingTimezone.value)
  }
  catch (e) {
    saveError.value = e instanceof Error ? e.message : String(e)
  }
  finally {
    isSaving.value = false
  }
}

async function onClear() {
  saveError.value = null
  isSaving.value = true
  try {
    await setUserTimezone('')
    // Reset the local picker to the auto-detected zone so the
    // preview matches the post-clear state.
    pendingTimezone.value = detectedTimezone
  }
  catch (e) {
    saveError.value = e instanceof Error ? e.message : String(e)
  }
  finally {
    isSaving.value = false
  }
}

// ----- Fallback timezone list ------------------------------------

/// Used when `Intl.supportedValuesOf` isn't available (old browsers).
/// Hand-curated to the most common zones so the user has a usable
/// list — they can fall back to typing the IANA string manually
/// later if they need an obscure zone.
const _FALLBACK_TIMEZONES = [
  'UTC',
  'America/New_York',
  'America/Chicago',
  'America/Denver',
  'America/Los_Angeles',
  'America/Anchorage',
  'America/Honolulu',
  'America/Toronto',
  'America/Vancouver',
  'America/Mexico_City',
  'America/Sao_Paulo',
  'America/Buenos_Aires',
  'Europe/London',
  'Europe/Dublin',
  'Europe/Paris',
  'Europe/Berlin',
  'Europe/Madrid',
  'Europe/Rome',
  'Europe/Amsterdam',
  'Europe/Stockholm',
  'Europe/Helsinki',
  'Europe/Athens',
  'Europe/Moscow',
  'Africa/Cairo',
  'Africa/Johannesburg',
  'Asia/Dubai',
  'Asia/Karachi',
  'Asia/Kolkata',
  'Asia/Bangkok',
  'Asia/Singapore',
  'Asia/Hong_Kong',
  'Asia/Shanghai',
  'Asia/Tokyo',
  'Asia/Seoul',
  'Australia/Perth',
  'Australia/Sydney',
  'Pacific/Auckland',
]
</script>
